import { useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Drawer, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MembershipRow } from '../../core/api/types';
import { permissionApi } from '../../core/authorization/api';
import { ACTION_ORDER, has, type ActionName } from '../../core/authorization/permissionMatrix';
import { grantsFor, nodesFor } from './permissionUi';

interface Props {
  open: boolean;
  product: string;
  productName: string;
  members: MembershipRow[];
  onClose: () => void;
}

type Effective = Awaited<ReturnType<typeof permissionApi.effective>>;

/** "Check a user": union of all the member's roles (plus open resources), read-only. */
export function EffectiveAccessDrawer({ open, product, productName, members, onClose }: Props) {
  const { t } = useTranslation();
  const [memberId, setMemberId] = useState('');
  const [data, setData] = useState<Effective | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const actionLabel = (a: ActionName) => t(`permissions.actions.${a}`, { defaultValue: a[0].toUpperCase() + a.slice(1) });

  async function pick(id: string) {
    const current = ++sequence.current;
    setMemberId(id); setData(null); setError(''); setLoading(true);
    try {
      const result = await permissionApi.effective(id, product);
      if (current === sequence.current) setData(result);
    } catch (e) {
      if (current === sequence.current) setError((e as Error).message);
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }

  const grants = data ? grantsFor(data.tree) : {};
  const via = (id: string, a: ActionName) => data?.sources.find((s) => s.resourceId === id)?.via[a] ?? [];
  const rows = data ? nodesFor(data.tree).flatMap((root) => [{ node: root, depth: 0 }, ...root.children.map((c) => ({ node: c, depth: 1 }))]) : [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="perm-drawer">
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">{t('permissions.checkUser', { defaultValue: 'Check a user' })}</Typography>
          <Button onClick={onClose}>{t('permissions.close', { defaultValue: 'Close' })}</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('permissions.checkUserHint', { defaultValue: 'What this member can do in {{product}}, from all of their roles together.', product: productName })}
        </Typography>
        <TextField select fullWidth size="small" label={t('permissions.member', { defaultValue: 'Member' })} value={memberId} onChange={(e) => void pick(e.target.value)}>
          {members.map((m) => <MenuItem key={m.id} value={m.id}>{`${m.firstName} ${m.lastName}`.trim() || m.userName} · {m.email}</MenuItem>)}
        </TextField>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
        {data && (
          <table className="perm-table compact" style={{ marginTop: 16 }}>
            <thead>
              <tr><th>{t('permissions.menu', { defaultValue: 'Menu' })}</th>{ACTION_ORDER.map((a) => <th key={a} className="act">{actionLabel(a)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map(({ node, depth }) => (
                <tr key={node.resourceId} className={depth === 0 ? 'root' : 'child'}>
                  <th scope="row" style={{ paddingLeft: 12 + depth * 20 }}>
                    {t(`menu.${node.label}`, { defaultValue: data.tree.find((r) => r.resourceId === node.resourceId)?.title || node.label })}
                  </th>
                  {ACTION_ORDER.map((a) => {
                    if (!has(node.supported, a)) return <td key={a}><span className="perm-na">—</span></td>;
                    const on = has(grants[node.resourceId] ?? 0, a);
                    const sources = via(node.resourceId, a);
                    const why = sources.includes('open') ? t('permissions.openToEveryone', { defaultValue: 'Open to everyone' })
                      : sources.length ? t('permissions.via', { defaultValue: 'via {{roles}}', roles: sources.join(', ') }) : '';
                    return (
                      <td key={a}>
                        <span className={`perm-chk static ${on ? 'on' : ''}`} title={why} aria-label={`${actionLabel(a)}: ${on ? why || 'yes' : 'no'}`}>
                          {on && <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" /></svg>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Drawer>
  );
}
